import classNames from "classnames";
import { memo, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { GridTableRow } from "../../../general/Tables/GridTable/GridTable";
import { CategoryFlattenedItem, CategoryFlattenedItemType } from "../utils";
import styles from "../Categories.module.scss";
import { PlusIcon } from "../../../../icons";
import { Button } from "../../../general/Button/Button";
import { PlainModal } from "../../../general/Modal/Modal";
import { CreateCategoryModal } from "../CreateCategoryModal";
import { useAllowFeature } from "../../../../hooks/useAllowFeature";
import { PREMIUM_FEATURES } from "../../../../common/flatRateBilling";
import { FiltersTypes } from "../../../../common/types/filters/general";
import { CategoryIdCell } from "./CategoryIdCell";

import { CategoryActions } from "./CategoryActions";
import stylesCategoryItem from "./CategoryItem.module.scss";

interface CategoryItemProps {
    item: CategoryFlattenedItem;
    collapsedCategories: Set<string>;
    setCollapsedCategories: (value: React.SetStateAction<Set<string>>) => void;
    projectedDepth?: number;
    percentage: number;
    transactionsCount: number;
    groupPercentage: number;
    groupTransactionsCount: number;
    filters: Pick<FiltersTypes, "end" | "start" | "entityIds" | "category">;
}

export const CategoryItem: React.FC<CategoryItemProps> = memo(
    ({
        item,
        collapsedCategories,
        setCollapsedCategories,
        projectedDepth,
        percentage,
        transactionsCount,
        groupPercentage,
        groupTransactionsCount,
        filters,
    }) => {
        const isCollapsible = useMemo(
            () => item.parentId === null && item.children.length > 0,
            [item],
        );

        const isCollapsed = useMemo(
            () => collapsedCategories.has(item.id as string),
            [collapsedCategories, item],
        );

        const percentageToDisplay = useMemo(
            () => (isCollapsed ? groupPercentage : percentage),
            [isCollapsed, groupPercentage, percentage],
        );
        const transactionsCountToDisplay = useMemo(
            () => (isCollapsed ? groupTransactionsCount : transactionsCount),
            [isCollapsed, groupTransactionsCount, transactionsCount],
        );

        const nameCell = useMemo(() => {
            const nameValue = (
                <CategoryIdCell
                    isCollapsed={isCollapsed}
                    item={item}
                    isCollapsible={isCollapsible}
                />
            );

            return nameValue;
        }, [item, isCollapsible, isCollapsed]);

        const transactionsLink = useMemo(
            () =>
                `/transactions?filters=${JSON.stringify({ ...filters, category: [item.id, ...(isCollapsed ? item.children.map((child) => child.id) : [])] })}`,
            [item.id, filters, isCollapsed, item.children],
        );

        const onClickCallback = useMemo(() => {
            let onClick = undefined;
            if (
                item.meta.type === CategoryFlattenedItemType.Category &&
                !item.parentId
            ) {
                onClick = () => {
                    setCollapsedCategories((prev) => {
                        const newSet = new Set(prev);
                        if (newSet.has(item.id as string)) {
                            newSet.delete(item.id as string);
                        } else {
                            newSet.add(item.id as string);
                        }
                        return newSet;
                    });
                };
            }
            return onClick;
        }, [item, setCollapsedCategories]);

        const description = useMemo(() => {
            if (!item.meta.description) {
                return "";
            }
            const trimmedDescription = item.meta.description.trim();
            const hasCommonExamples =
                trimmedDescription.includes("Common examples:");

            if (!hasCommonExamples) {
                return item.meta.description;
            }
            const indexOfCommonExamples =
                trimmedDescription.indexOf("Common examples:");
            return trimmedDescription.slice(0, indexOfCommonExamples);
        }, [item.meta.description]);

        const cells = useMemo(
            () => [
                {
                    key: "name",
                    value: nameCell,
                },
                {
                    key: "description",
                    value: (
                        <OverlayTrigger
                            overlay={
                                <Tooltip id="category-description">
                                    <span style={{ whiteSpace: "pre-line" }}>
                                        {item.meta.description}
                                    </span>
                                </Tooltip>
                            }
                        >
                            <div className={styles.description}>
                                {description}
                            </div>
                        </OverlayTrigger>
                    ),
                },
                {
                    key: "value",
                    value: !isNaN(percentageToDisplay) && (
                        <span
                            className={classNames(
                                stylesCategoryItem.label,
                                percentageToDisplay === 0 &&
                                    stylesCategoryItem.zero,
                            )}
                        >
                            {percentageToDisplay.toFixed(1)}%
                        </span>
                    ),
                },
                {
                    key: "transactions",
                    value:
                        !isNaN(transactionsCountToDisplay) &&
                        transactionsCountToDisplay > 0 ? (
                            <a
                                href={transactionsLink}
                                target="_blank"
                                className={classNames(
                                    stylesCategoryItem.label,
                                    stylesCategoryItem.linkHover,
                                )}
                                onClick={(event) => event.stopPropagation()}
                            >
                                {transactionsCountToDisplay}
                            </a>
                        ) : (
                            <span
                                className={classNames(
                                    stylesCategoryItem.label,
                                    stylesCategoryItem.zero,
                                )}
                            >
                                0
                            </span>
                        ),
                },
                {
                    key: "actions",
                    value:
                        item.meta.type ===
                        CategoryFlattenedItemType.CustomCategory ? (
                            <CategoryActions
                                category={item}
                                transactionsCount={transactionsCount}
                            />
                        ) : (
                            <AddCategoryIndicator category={item} />
                        ),
                },
            ],
            [
                item,
                nameCell,
                description,
                transactionsLink,
                transactionsCount,
                percentageToDisplay,
                transactionsCountToDisplay,
            ],
        );

        return (
            <GridTableRow
                onClick={onClickCallback}
                key={item.id}
                cells={cells}
                className={classNames(styles.row, {
                    [styles.subcategoryRow]:
                        !!item.parentId ||
                        (projectedDepth && projectedDepth > 0),
                    [styles.bottomBorder]:
                        (!item.parentId &&
                            (!!collapsedCategories.has(item.id as string) ||
                                item.children.length === 0)) ||
                        (item.parentId &&
                            !collapsedCategories.has(item.parentId as string) &&
                            item.isLast),
                    [styles.isCollapsed]:
                        collapsedCategories.has(item.parentId as string) ||
                        item.meta.isHidden,
                    [styles.isCollapsible]: isCollapsible,
                })}
                columnBorders={false}
            />
        );
    },
);

interface AddCategoryIndicatorProps {
    category: CategoryFlattenedItem;
}

function AddCategoryIndicator({ category }: AddCategoryIndicatorProps) {
    const [showCreateCategoryModal, setShowCreateCategoryModal] =
        useState(false);

    const allowCreateNewCustomCategory = useAllowFeature(
        PREMIUM_FEATURES.CUSTOM_CATEGORIES,
    );

    return allowCreateNewCustomCategory &&
        category.parentId === null &&
        !category.disableAddChildren ? (
        <>
            <Button
                icon
                size="sm"
                variant="tertiary"
                onClick={(e) => {
                    e.stopPropagation();
                    setShowCreateCategoryModal(true);
                }}
                tooltip="Add subcategory"
            >
                <PlusIcon />
            </Button>

            <PlainModal
                show={showCreateCategoryModal}
                onHide={() => setShowCreateCategoryModal(false)}
            >
                <CreateCategoryModal
                    parentCategoryId={category.id as string}
                    onHide={() => setShowCreateCategoryModal(false)}
                />
            </PlainModal>
        </>
    ) : null;
}
